import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { DatePicker, Input, Select } from 'antd';
import { DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Controller } from 'react-hook-form';

import { useSelector } from 'react-redux';
import GetField from '../Components/GetField';
import styles from '../styles';
import { getGrandTotal } from './common-functions';

const AntSelect = GetField(Select);
const AntInput = GetField(Input);
const { Option } = Select;
const AntDatePicker = GetField(DatePicker);

const ProductStyleForm = props => {
  const {
    classes,
    getValues,
    setValue,
    control,
    watch,
    styleItem,
    styleIndex,
    styleAppend,
    styleRemove,
  } = props;

  const stylesTable = useSelector(state => state.jobchartStylesForRpo);
  const unitIds = useSelector(state => state.unitIds);

  const stylesArray = useMemo(() => {
    const productId = getValues(`styles[${styleIndex}].productId`);

    if (stylesTable.data.styles[productId] !== undefined) {
      return stylesTable.data.styles[productId].styleNames || [];
    }
    return [];
  }, [stylesTable, watch(`styles[${styleIndex}].productId`)]);

  const descriptionsArray = useMemo(() => {
    const productId = getValues(`styles[${styleIndex}].productId`);
    const style = getValues(`styles[${styleIndex}].style`);

    if (stylesTable.data.styles[productId] !== undefined) {
      return stylesTable.data.styles[productId].descriptions[style] || [];
    }
    return [];
  }, [
    stylesTable,
    watch(`styles[${styleIndex}].productId`),
    watch(`styles[${styleIndex}].style`),
  ]);

  const sizesArray = useMemo(() => {
    const productId = getValues(`styles[${styleIndex}].productId`);
    const style = getValues(`styles[${styleIndex}].style`);
    const description = getValues(`styles[${styleIndex}].description`);

    if (stylesTable.data.styles[productId]?.sizes[style] !== undefined) {
      return stylesTable.data.styles[productId].sizes[style][description] || [];
    }
    return [];
  }, [
    stylesTable,
    watch(`styles[${styleIndex}].productId`),
    watch(`styles[${styleIndex}].style`),
    watch(`styles[${styleIndex}].description`),
  ]);

  const baseColorsArray = useMemo(() => {
    const productId = getValues(`styles[${styleIndex}].productId`);
    const styleId = getValues(`styles[${styleIndex}].styleId`);

    if (stylesTable.data.styles[productId] !== undefined) {
      return stylesTable.data.styles[productId].baseColors[styleId] || [];
    }
    return [];
  }, [
    stylesTable,
    watch(`styles[${styleIndex}].productId`),
    watch(`styles[${styleIndex}].style`),
    watch(`styles[${styleIndex}].description`),
    watch(`styles[${styleIndex}].styleId`),
  ]);

  const printColorsArray = useMemo(() => {
    const productId = getValues(`styles[${styleIndex}].productId`);
    const styleId = getValues(`styles[${styleIndex}].styleId`);
    const baseColorId = getValues(`styles[${styleIndex}].baseColorId`);

    if (
      stylesTable.data.styles[productId]?.printColors[styleId]?.[baseColorId]
    ) {
      return (
        stylesTable.data.styles[productId].printColors[styleId][baseColorId] ||
        []
      );
    }
    return [];
  }, [
    stylesTable,
    watch(`styles[${styleIndex}].productId`),
    watch(`styles[${styleIndex}].style`),
    watch(`styles[${styleIndex}].description`),
    watch(`styles[${styleIndex}].styleId`),
    watch(`styles[${styleIndex}].baseColorId`),
  ]);

  const embroideryColorsArray = useMemo(() => {
    const productId = getValues(`styles[${styleIndex}].productId`);
    const styleId = getValues(`styles[${styleIndex}].styleId`);
    const baseColorId = getValues(`styles[${styleIndex}].baseColorId`);
    const printColorId = getValues(`styles[${styleIndex}].printColorId`);

    if (
      stylesTable.data.styles[productId]?.embroideryColors[styleId]?.[
        baseColorId
      ]?.[printColorId]
    ) {
      return (
        stylesTable.data.styles[productId].embroideryColors[styleId][
          baseColorId
        ][printColorId] || []
      );
    }
    return [];
  }, [
    stylesTable,
    watch(`styles[${styleIndex}].productId`),
    watch(`styles[${styleIndex}].style`),
    watch(`styles[${styleIndex}].description`),
    watch(`styles[${styleIndex}].styleId`),
    watch(`styles[${styleIndex}].baseColorId`),
    watch(`styles[${styleIndex}].printColorId`),
  ]);

  const acidWashColorsArray = useMemo(() => {
    const productId = getValues(`styles[${styleIndex}].productId`);
    const styleId = getValues(`styles[${styleIndex}].styleId`);
    const baseColorId = getValues(`styles[${styleIndex}].baseColorId`);
    const printColorId = getValues(`styles[${styleIndex}].printColorId`);
    const embroideryColorId = getValues(
      `styles[${styleIndex}].embroideryColorId`
    );

    if (
      stylesTable.data.styles[productId]?.acidWashColors[styleId]?.[
        baseColorId
      ]?.[printColorId]?.[embroideryColorId]
    ) {
      return (
        stylesTable.data.styles[productId].acidWashColors[styleId][baseColorId][
          printColorId
        ][embroideryColorId] || []
      );
    }
    return [];
  }, [
    stylesTable,
    watch(`styles[${styleIndex}].productId`),
    watch(`styles[${styleIndex}].style`),
    watch(`styles[${styleIndex}].description`),
    watch(`styles[${styleIndex}].styleId`),
    watch(`styles[${styleIndex}].baseColorId`),
    watch(`styles[${styleIndex}].printColorId`),
    watch(`styles[${styleIndex}].embroideryColorId`),
  ]);

  useEffect(() => {
    setValue(
      `styles[${styleIndex}].jobchartQuantity`,
      stylesTable.quantities[
        `P${watch(`styles[${styleIndex}].productId`)}S${watch(
          `styles[${styleIndex}].styleId`
        )}B${watch(`styles[${styleIndex}].baseColorId`)}P${
          watch(`styles[${styleIndex}].printColorId`) || ''
        }E${watch(`styles[${styleIndex}].embroideryColorId`) || ''}A${
          watch(`styles[${styleIndex}].acidWashColorId`) || ''
        }`
      ] || 0
    );
  }, [
    stylesTable,
    watch(`styles[${styleIndex}].productId`),
    watch(`styles[${styleIndex}].style`),
    watch(`styles[${styleIndex}].description`),
    watch(`styles[${styleIndex}].styleId`),
    watch(`styles[${styleIndex}].baseColorId`),
    watch(`styles[${styleIndex}].printColorId`),
    watch(`styles[${styleIndex}].embroideryColorId`),
    watch(`styles[${styleIndex}].acidWashColorId`),
  ]);

  useEffect(() => {
    const quantity = getValues(`styles[${styleIndex}].quantity`) || 0;
    const rate = getValues(`styles[${styleIndex}].rate`) || 0;
    const gst = getValues(`styles[${styleIndex}].gst`) || 0;

    const value = +(+quantity * +rate).toFixed(2) || 0;
    const gstValue = +((+value * +gst) / 100).toFixed(2);
    const total = +(+value + +gstValue || 0).toFixed(2);
    const singleTotal = +(total / (quantity || 1)).toFixed(2);

    setValue(`styles[${styleIndex}].value`, value);
    setValue(`styles[${styleIndex}].gstValue`, gstValue);
    setValue(`styles[${styleIndex}].singleTotal`, singleTotal);
    setValue(`styles[${styleIndex}].total`, total);
  }, [
    watch(`styles[${styleIndex}].quantity`),
    watch(`styles[${styleIndex}].rate`),
    watch(`styles[${styleIndex}].gst`),
  ]);

  useEffect(() => {
    const fabricItems = getValues('fabrics');
    const styleItems = getValues('styles');
    const items = getValues('items');
    const stylesItems = getValues('styles');
    setValue(
      'grandTotal',
      getGrandTotal(fabricItems, styleItems, items, stylesItems)
    );
  }, [watch(`styles[${styleIndex}].total`)]);

  return (
    <tr>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].productId`}
          control={control}
          defaultValue={styleItem.productId}
          render={({ field }) => (
            <AntSelect placeholder="Select product" {...field}>
              {stylesTable.data.products.map(({ name, id }) => (
                <Option key={id} value={id}>
                  {name}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].style`}
          control={control}
          defaultValue={styleItem.style}
          render={({ field }) => (
            <AntSelect placeholder="Select style" {...field}>
              {stylesArray.map(style => (
                <Option key={style} value={style}>
                  {style}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].description`}
          control={control}
          defaultValue={styleItem.description}
          render={({ field }) => (
            <AntSelect placeholder="Select description" {...field}>
              {descriptionsArray.map(description => (
                <Option key={description} value={description}>
                  {description}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].styleId`}
          control={control}
          defaultValue={styleItem.styleId}
          render={({ field }) => (
            <AntSelect placeholder="Select style" {...field}>
              {sizesArray.map(size => (
                <Option key={size.id} value={size.id}>
                  {size.size}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].baseColorId`}
          control={control}
          defaultValue={styleItem.baseColorId}
          render={({ field }) => (
            <AntSelect placeholder="Select color" {...field}>
              {baseColorsArray.map(color => (
                <Option key={color.id} value={color.id}>
                  {color.color}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].printColorId`}
          control={control}
          defaultValue={styleItem.printColorId}
          render={({ field }) => (
            <AntSelect placeholder="Select color" {...field}>
              {printColorsArray.map(color => (
                <Option key={color.id} value={color.id}>
                  {color.color}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].embroideryColorId`}
          control={control}
          defaultValue={styleItem.embroideryColorId}
          render={({ field }) => (
            <AntSelect placeholder="Select color" {...field}>
              {embroideryColorsArray.map(color => (
                <Option key={color.id} value={color.id}>
                  {color.color}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].acidWashColorId`}
          control={control}
          defaultValue={styleItem.acidWashColorId}
          render={({ field }) => (
            <AntSelect placeholder="Select color" {...field}>
              {acidWashColorsArray.map(color => (
                <Option key={color.id} value={color.id}>
                  {color.color}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].jobchartQuantity`}
          control={control}
          defaultValue={styleItem.jobchartQuantity}
          render={({ field }) => (
            <AntInput
              {...field}
              placeholder="Jobchart Qty"
              disabled
              type="number"
              min={0}
              size="small"
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].quantity`}
          control={control}
          defaultValue={styleItem.quantity}
          render={({ field }) => (
            <AntInput
              placeholder="Quantity"
              {...field}
              type="number"
              min={0}
              size="small"
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].unitId`}
          control={control}
          defaultValue={styleItem.unitId}
          render={({ field }) => (
            <AntSelect placeholder="Unit" {...field}>
              {unitIds.map(unit => (
                <Option key={unit.id} value={unit.id}>
                  {unit.unit}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].rate`}
          control={control}
          defaultValue={styleItem.rate}
          render={({ field }) => (
            <AntInput
              placeholder="Price"
              {...field}
              type="number"
              min={0}
              size="small"
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].value`}
          control={control}
          defaultValue={styleItem.value}
          render={({ field }) => (
            <AntInput
              placeholder="Amount"
              {...field}
              type="number"
              min={0}
              size="small"
              disabled
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].gst`}
          control={control}
          defaultValue={styleItem.gst}
          render={({ field }) => (
            <AntInput
              placeholder="GST (%)"
              {...field}
              type="number"
              min={0}
              size="small"
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].gstValue`}
          control={control}
          defaultValue={styleItem.gstValue}
          render={({ field }) => (
            <AntInput
              placeholder="GST Amount"
              {...field}
              type="number"
              min={0}
              size="small"
              disabled
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].singleTotal`}
          control={control}
          defaultValue={styleItem.singleTotal}
          render={({ field }) => (
            <AntInput
              placeholder="Total"
              {...field}
              type="number"
              min={0}
              size="small"
              disabled
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].total`}
          control={control}
          defaultValue={styleItem.total}
          render={({ field }) => (
            <AntInput
              placeholder="Total"
              {...field}
              type="number"
              min={0}
              size="small"
              disabled
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].hsn`}
          control={control}
          defaultValue={styleItem.hsn}
          render={({ field }) => <AntInput placeholder="HSN Code" {...field} />}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].deliveryDate`}
          control={control}
          render={({ field }) => (
            <AntDatePicker allowClear placeholder="Date" {...field} />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <DeleteOutlined
          onClick={() => {
            styleRemove(styleIndex);
          }}
        />
        &nbsp; &nbsp;
        <PlusCircleOutlined
          onClick={() =>
            styleAppend({
              productId: null,
              styleId: null,
              baseColorId: null,
              printColorId: null,
              embroideryColorId: null,
              acidWashColorId: null,
            })
          }
        />
      </td>
    </tr>
  );
};

ProductStyleForm.propTypes = {
  classes: PropTypes.object.isRequired,
  getValues: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  control: PropTypes.any.isRequired,
  watch: PropTypes.func.isRequired,
  styleRemove: PropTypes.func.isRequired,
  styleAppend: PropTypes.func.isRequired,
  styleItem: PropTypes.object.isRequired,
  styleIndex: PropTypes.number.isRequired,
};

export default withStyles(styles)(ProductStyleForm);
